import type { ApiErrorBody, ApiErrorCode } from '@lumo/shared';

import { tr, trf } from '@/i18n';
export class HttpError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode | 'network_error';
  readonly details?: unknown;

  constructor(status: number, code: HttpError['code'], message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]!) : null;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Сырой ответ (Blob) вместо JSON. */
  raw?: boolean;
}

/** Запрос к API: JSON, CSRF-заголовок для изменяющих методов, единый формат ошибок. */
export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? (opts.body !== undefined ? 'POST' : 'GET');
  const url = new URL(path, location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  const headers: Record<string, string> = { accept: 'application/json' };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = readCookie('lumo_csrf');
    if (csrf) headers['x-csrf-token'] = csrf;
  }
  let res: Response;
  try {
    res = await fetch(url, { method, headers, body, credentials: 'same-origin', signal: opts.signal });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new HttpError(0, 'network_error', tr("Нет соединения с сервером"));
  }
  if (opts.raw && res.ok) return (await res.blob()) as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const e = (data as ApiErrorBody | null)?.error;
    throw new HttpError(res.status, e?.code ?? 'internal_error', e?.message ?? trf('Ошибка {status}', { status: res.status }), e?.details);
  }
  return data as T;
}

/** Текст ошибки для тоста/формы. */
export function errorMessage(err: unknown): string {
  if (err instanceof HttpError) {
    // Сервер отвечает по-русски — переводим через тот же словарь, что и интерфейс.
    if (err.code === 'validation_failed' && Array.isArray(err.details) && err.details[0]?.message) return tr(String(err.details[0].message));
    return tr(err.message);
  }
  if (err instanceof Error) return tr(err.message);
  return tr("Что-то пошло не так");
}

/** Поле, к которому относится ошибка валидации. */
export function errorField(err: unknown): string | null {
  if (!(err instanceof HttpError)) return null;
  const d = err.details as { field?: string } | Array<{ path?: string }> | undefined;
  if (Array.isArray(d)) return d[0]?.path ?? null;
  return d?.field ?? null;
}
